import { Button, cn } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'

/**
 * Which failure an {@link ErrorState} describes (TO-639, ux-interaction-standards §3). Each variant
 * carries its own default message; the icon and recovery affordance are shared across all three.
 */
export enum ErrorStateVariant {
	/** The request never reached the server (offline, timeout, DNS). Retry is the usual recovery. */
	NETWORK = 'network',
	/** The server answered with a failure (5xx, unexpected shape). Retry may succeed later. */
	SERVER = 'server',
	/** The submitted data is not acceptable (4xx validation). The viewer must change their input. */
	VALIDATION = 'validation',
}

/**
 * Built-in strings of {@link ErrorState}, overridable per locale. Each `*Message` is a variant's
 * default copy, used only when the consumer omits `message`; `retry` labels the recovery button.
 */
export interface ErrorStateLabels {
	networkMessage: string
	serverMessage: string
	validationMessage: string
	retry: string
}

const DEFAULT_LABELS: ErrorStateLabels = {
	networkMessage: "We couldn't reach the server. Check your connection and try again.",
	serverMessage: 'Something went wrong on our side. Please try again.',
	validationMessage: 'Some details need fixing before you can continue.',
	retry: 'Retry',
}

/** The label key holding each variant's default message. */
const VARIANT_MESSAGE: Record<ErrorStateVariant, keyof ErrorStateLabels> = {
	[ErrorStateVariant.NETWORK]: 'networkMessage',
	[ErrorStateVariant.SERVER]: 'serverMessage',
	[ErrorStateVariant.VALIDATION]: 'validationMessage',
}

/**
 * Props for the {@link ErrorState} molecule.
 *
 * @property variant - Which failure this is; defaults to `network` (see {@link ErrorStateVariant}).
 * @property title - Optional heading above the message; when omitted only the message renders.
 * @property message - The error text. When omitted the variant's default message renders.
 * @property onRetry - Handler of the Retry button; the button renders only when it is supplied,
 *   because a retry that cannot do anything would mislead the viewer.
 * @property labels - Localised built-in strings; each omitted key falls back to English.
 * @property className - Extra Tailwind classes merged onto the container.
 *
 * @example
 * ```tsx
 * <ErrorState variant={ErrorStateVariant.NETWORK} onRetry={refetch} />
 * ```
 */
export interface ErrorStateProps {
	variant?: ErrorStateVariant
	title?: string
	message?: ReactNode
	onRetry?: () => void
	labels?: Partial<ErrorStateLabels>
	className?: string
}

/**
 * Warning triangle marking the region as an error. Decorative — the alert role and the message
 * carry the meaning for assistive tech, so the icon is `aria-hidden` and tinted with `ds-danger`.
 */
function ErrorIcon(): ReactElement {
	return (
		<span data-testid="error-state-icon" aria-hidden="true" className="text-ds-danger">
			{/* `aria-hidden` here is redundant with the wrapping span's, but Biome's noSvgWithoutTitle
			    requires it on a title-less <svg>. The rule's "off" override in biome.json is scoped to
			    test/story globs only, so it is still ON for this `.view.tsx` source file. */}
			<svg
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				aria-hidden="true"
			>
				<path d="M12 9v4" strokeLinecap="round" />
				<path d="M12 17h.01" strokeLinecap="round" />
				<path
					d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
					strokeLinejoin="round"
				/>
			</svg>
		</span>
	)
}

/**
 * Inline, recoverable error shown where a section's content would be, distinct from the full-page
 * HTTP error pages (W1-10). The counterpart to {@link EmptyState}.
 *
 * @remarks
 * - **Variants:** `network`, `server` and `validation` each carry a distinct default message; the
 *   consumer's `message` overrides it. `data-variant` on the container names the variant.
 * - **Recovery:** the Retry button renders only when `onRetry` is supplied; it is the primitives-
 *   layer `<Button>`.
 * - **Accessibility:** the container is `role="alert"`, so a screen reader announces the error when
 *   it appears; the icon is decorative (`aria-hidden`).
 * - **Styling:** every colour, radius, spacing and type class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/error-state/error-state.stories.tsx for visual examples
 */
export function ErrorState({
	variant = ErrorStateVariant.NETWORK,
	title,
	message,
	onRetry,
	labels,
	className,
}: ErrorStateProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...labels }
	const body = message ?? text[VARIANT_MESSAGE[variant]]
	return (
		<div
			role="alert"
			data-variant={variant}
			className={cn(
				'flex flex-col items-center gap-3 rounded-ds-lg border border-ds-border p-8 text-center',
				className,
			)}
		>
			<ErrorIcon />
			{title ? <h3 className="text-ds-heading-3 text-ds-text">{title}</h3> : null}
			<p data-testid="error-state-message" className="text-ds-body text-ds-text-muted">
				{body}
			</p>
			{onRetry ? (
				<Button size="sm" variant="secondary" onPress={onRetry}>
					{text.retry}
				</Button>
			) : null}
		</div>
	)
}
